import { SETTINGS_ROUTES } from '../../../config/api-routes';
import instance from '../../../config/instance';
import { paramsToQueryString } from '../../../utility/helpers/commonFunction';

/**
 * Get shop configuration details
 * This API fetches the main shop configuration including business details,
 * timezone, currency, and other core settings
 * @returns {Promise} - Shop configuration data
 */
export const getShopConfigApi = async () => {
    const response = await instance.get(SETTINGS_ROUTES.GET_SHOP_CONFIG);
    return response.data.data;
};

/**
 * Get pdf settings visibility
 * This API fetches the main shop configuration including business details,
 * timezone, currency, and other core settings
 * @returns {Promise} - Shop configuration data
 */
export const getvisibilitySettingApi = async () => {
    const response = await instance.get(SETTINGS_ROUTES.GET_VISIBILITY_SETTING);
    return response.data.data;
};

// update pdf setting visibility
export const updatevisibilitySettingApi = async (payload) => {
    const response = await instance.put(SETTINGS_ROUTES.UPDATE_VISIBILITY_SETTING, payload);
    return response.data;
};

/**
 * Get terms and warranty data
 * This API fetches terms and warranty information for invoices and estimates
 * @returns {Promise} - Terms and warranty data
 */
export const getTermsAndWarrantyApi = async () => {
    const response = await instance.get(SETTINGS_ROUTES.GET_TERMS_AND_WARRANTY);
    return response.data.data || response.data;
};

/**
 * Get Shop personal detail
 * This API fetches shop profile detail information for invoices and estimates
 * @returns {Promise} - Shop profile detail
 */
export const getShopProfileDetailApi = async () => {
    const response = await instance.get(SETTINGS_ROUTES.GET_SHOP_PROFILE_DETAIL);
    return response.data.data || response.data;
};

/**
 * Get discount programs list
 * This API fetches all membership/discount programs
 * @param {Object} params - Query parameters (e.g., { pagination: false })
 * @returns {Promise} - Discount programs data
 */
export const getDiscountProgramsApi = async (params = {}) => {
    const queryString = paramsToQueryString(params);
    const response = await instance.get(`${SETTINGS_ROUTES.GET_DISCOUNT_PROGRAMS}${queryString}`);
    return response.data.data;
};

/**
 * Get shop module sequence configuration
 * This API fetches invoice/estimate/appointment sequence numbers and prefixes
 * @returns {Promise} - Sequence configuration data
 */
export const getSequenceNumberConfigApi = async () => {
    const response = await instance.get(SETTINGS_ROUTES.GET_SEQUENCE_NUMBER);
    return response.data.data;
};

/**
 * Update shop module sequence configuration
 * This API updates invoice/estimate/appointment sequence numbers
 * @param {Object} payload - Sequence configuration payload
 * @returns {Promise} - Update response
 */
export const updateSequenceNumberConfigApi = async (payload) => {
    const response = await instance.patch(SETTINGS_ROUTES.UPDATE_SEQUENCE_NUMBER, payload);
    return response.data.data || response.data;
};

export const updateShopProfileDetailApi = async (payload) => {
    const response = await instance.put(SETTINGS_ROUTES.UPDATE_SHOP_PROFILE_DETAIL, payload);
    return response.data.data;
};

/**
 * Update buffer configuration
 * This API updates the buffer days settings for online booking
 * @param {Object} payload - Buffer configuration data
 * @returns {Promise} - Update response
 */
export const updateBufferConfigApi = async (payload) => {
    const response = await instance.put(SETTINGS_ROUTES.UPDATE_BUFFER_CONFIG, payload);
    return response.data.data;
};

/**
 * Update shop configuration
 * This API updates general shop configuration settings
 * @param {Object} payload - Shop configuration data
 * @returns {Promise} - Update response
 */
export const updateShopConfigApi = async (payload) => {
    const response = await instance.put(SETTINGS_ROUTES.UPDATE_SHOP_CONFIG, payload);
    return response.data.data;
};

/**
 * Get email thank you details
 * This API fetches email thank you settings
 * @returns {Promise} - Email thank you data
 */
export const getEmailThankYouDetailsApi = async () => {
    const response = await instance.get(SETTINGS_ROUTES.GET_THANK_YOU_EMAIL);
    return response.data.data;
};

/**
 * Update email thank you details
 * This API updates email thank you settings
 * @param {Object} payload - Email thank you data
 * @returns {Promise} - Update response
 */
export const updateEmailThankYouDetailsApi = async (payload) => {
    const response = await instance.put(SETTINGS_ROUTES.UPDATE_THANK_YOU_EMAIL, payload);
    return response.data.data;
};

/**
 * Send test thank you email
 * This API sends a test thank you email
 * @param {Object} payload - Test email data
 * @returns {Promise} - Send response
 */
export const sendTestThankYouEmailApi = async (payload) => {
    const response = await instance.post(SETTINGS_ROUTES.SEND_TEST_THANK_YOU_EMAIL, payload);
    return response.data.data;
};

/**
 * Update drop-off slots configuration
 * This API updates the drop-off slots configuration settings
 * @param {Object} payload - Drop-off slots configuration data
 * @returns {Promise} - Update response
 */
export const updateDropOffSlotsConfigApi = async (payload) => {
    const response = await instance.put(SETTINGS_ROUTES.UPDATE_DROP_OFF_SLOTS_CONFIG, payload);
    return response.data;
};

/**
 * Get available slots with disabled status for a specific date
 * This API fetches the available time slots for a given date including disabled slots
 * Uses POST method with shopId and date in the request body
 * @param {Object} payload - Payload containing shopId and date
 * @param {string} payload.shopId - Shop ID
 * @param {string} payload.date - Date in YYYY-MM-DD format
 * @returns {Promise} - Response with date and slots array containing startDateTime, endDateTime, isLimitReached, and isSlotDisabled
 */
export const getSlotsWithDisabledApi = async ({ shopId, date, includeAppointmentCount }) => {
    const response = await instance.post(SETTINGS_ROUTES.GET_SLOTS_WITH_DISABLED, {
        shopId,
        date,
        includeAppointmentCount,
    });
    return response.data.data || response.data;
};

/**
 * Update disabled slots for a specific date
 * This API updates the disabled status of time slots for a given date
 * Uses PUT method with date and slots array in the request body
 * @param {Object} payload - Payload containing date and slots array
 * @param {string} payload.date - Date in YYYY-MM-DD format
 * @param {Array} payload.slots - Array of slot objects with startDateTime, endDateTime, isLimitReached, and isSlotDisabled
 * @returns {Promise} - Update response
 */
export const updateDisabledSlotsApi = async (payload) => {
    const response = await instance.put(SETTINGS_ROUTES.UPDATE_DISABLED_SLOTS, payload);
    return response.data.data || response.data;
};

/**
 * Get all disabled slots
 * This API fetches the list of all disabled slots
 * @returns {Promise} - Response with array of disabled slots
 */
export const getDisabledSlotsApi = async () => {
    const response = await instance.get(SETTINGS_ROUTES.GET_DISABLED_SLOTS);
    return response.data.data || response.data;
};

/**
 * Delete a disabled slot by ID
 * This API deletes a specific disabled slot
 * @param {string} id - Disabled slot ID
 * @returns {Promise} - Delete response
 */
export const deleteDisabledSlotApi = async (id) => {
    const response = await instance.delete(SETTINGS_ROUTES.DELETE_DISABLED_SLOT(id));
    return response.data.data || response.data;
};

/**
 * Get available dates for a specific month and year
 * This API fetches the available dates for booking in a given month/year
 * Uses POST method with shopId, year, and month in the request body
 * @param {Object} payload - Payload containing shopId, year, and month
 * @param {string} payload.shopId - Shop ID
 * @param {number} payload.year - Year (e.g., 2025)
 * @param {number} payload.month - Month (1-12)
 * @returns {Promise} - Response with array of date objects containing date and isAvailable
 */
export const getAvailableDatesApi = async ({ shopId, year, month, includeAppointmentCount }) => {
    const response = await instance.post(SETTINGS_ROUTES.GET_AVAILABLE_DATES, {
        shopId,
        year,
        month,
        includeAppointmentCount,
    });
    return response.data.data || response.data;
};

/**
 * Get appointments by slot
 * This API fetches appointments for a specific slot on a given date
 * Uses POST method with shopId, date, and slotId in the request body
 * @param {Object} payload - Payload containing shopId, date, and slotId
 * @param {string} payload.shopId - Shop ID
 * @param {string} payload.date - Date in YYYY-MM-DD format
 * @param {string} payload.slotId - Slot ID
 * @returns {Promise} - Response with array of appointment objects
 */
export const getAppointmentsBySlotApi = async ({ shopId, date, slotId }) => {
    const response = await instance.post(SETTINGS_ROUTES.GET_APPOINTMENTS_BY_SLOT, {
        shopId,
        date,
        slotId,
    });
    return response.data.data || response.data;
};

/**
 * Get shop taxes list
 * @returns {Promise} - Shop tax document with taxRules
 */
export const getShopTaxesApi = async () => {
    const response = await instance.get(SETTINGS_ROUTES.GET_SHOP_TAXES);
    const data = response.data?.data ?? response.data;
    return data?.json ?? data;
};

/**
 * Update shop taxes
 * @param {Object} params
 * @param {string} params.id - Shop tax document ID
 * @param {Object} params.payload - Full shop tax update payload
 * @returns {Promise} - Update response
 */
export const updateShopTaxesApi = async ({ id, payload }) => {
    const response = await instance.put(SETTINGS_ROUTES.UPDATE_SHOP_TAXES(id), payload);
    return response.data;
};

/**
 * Get customer notification templates
 * Method: GET
 * Route: web/v4/api/customer-notification-templates
 * @returns {Promise} - Customer notification templates data
 */
export const getCustomerNotificationTemplatesApi = async () => {
    const response = await instance.get(SETTINGS_ROUTES.CUSTOMER_NOTIFICATION_TEMPLATES);
    return response.data.data || response.data;
};

/**
 * Update customer notification templates
 * Method: PUT
 * Route: web/v4/api/customer-notification-templates
 * @param {Object} payload - Notification templates update payload
 * @returns {Promise} - Update response data
 */
export const updateCustomerNotificationTemplatesApi = async (payload) => {
    const response = await instance.put(SETTINGS_ROUTES.CUSTOMER_NOTIFICATION_TEMPLATES, payload);
    return response.data.data || response.data;
};
